import { selectStandardProps } from "@/utils/componentUtils";
import { Form, Select } from "antd";
import { FormItemProps } from "antd/lib/form";
import { SelectProps } from "antd/lib/select";
import { DEFAULT_LOCALE } from "../../../../app/i18n";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { ProductFinancialSector } from "../../../product/enums";
import { ProductGroupBase } from "../../../product/types";
import { EnumerationType } from "../../enums";
import { FilterFunction } from "../../types";

interface Props {
  formItemProps: FormItemProps;
  selectProps?: SelectProps<string>;
  optionsProps?: ProductGroupOptionsProps;
}

interface ProductGroupOptionsProps {
  selectedGroupId?: UUID;
  selectedProductId?: UUID;
  institutionId?: UUID;
  hideAll?: boolean;
  initialValuesSelected?: boolean;
  filterSectors?: ProductFinancialSector[];
  filter?: FilterFunction<ProductGroupBase>;
}

const ProductGroupSelect = ({ formItemProps, selectProps, optionsProps }: Props) => {
  const productsEnums = useEnumeration(EnumerationType.PRODUCTS);
  const productGroups = useEnumeration(EnumerationType.PRODUCT_GROUPS);

  const resolveProductGroupSelectOptions = () => {
    if (optionsProps?.hideAll) {
      return [];
    }

    let groups: ProductGroupBase[] = [...productGroups];

    if (optionsProps?.institutionId) {
      const productGroupIds = [
        ...new Set(
          productsEnums
            .filter((product) => product.institutionIds.includes(optionsProps.institutionId!))
            .map((product) => product.productGroupId),
        ),
      ];

      groups = groups.filter((group) => productGroupIds.includes(group.id));
    }

    if (optionsProps?.filterSectors?.length) {
      const productGroupIds = productsEnums
        .filter((product) => product.sector && optionsProps.filterSectors?.includes(product.sector))
        .map((product) => product.productGroupId);

      groups = groups.filter((group) => productGroupIds.includes(group.id));
    }

    if (optionsProps?.filter) {
      groups = groups.filter(optionsProps.filter);
    }

    if (
      optionsProps?.selectedGroupId &&
      optionsProps.initialValuesSelected &&
      !groups.some((group) => group.id === optionsProps.selectedGroupId)
    ) {
      const productGroup = productGroups.find((group) => group.id === optionsProps.selectedGroupId);

      if (productGroup) {
        groups.unshift(productGroup);
      }
    }

    if (
      optionsProps?.selectedProductId &&
      optionsProps.initialValuesSelected &&
      !groups.some(
        (group) =>
          group.id === productsEnums.find((product) => product.id === optionsProps.selectedProductId)?.productGroupId,
      )
    ) {
      const groupId = productsEnums.find((product) => product.id === optionsProps.selectedProductId)?.productGroupId;
      const group = productGroups.find((group) => group.id === groupId);

      if (group) {
        groups.push(group);
      }
    }

    return groups.sort((a, b) => a.name.localeCompare(b.name, DEFAULT_LOCALE, { sensitivity: "accent" }));
  };

  return (
    <Form.Item {...formItemProps}>
      <Select
        {...selectStandardProps}
        {...selectProps}
        options={resolveProductGroupSelectOptions().map((g) => ({ value: g.id, label: g.name }))}
      />
    </Form.Item>
  );
};

export default ProductGroupSelect;
